import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';

import CustomerBanner from '@/components/molecules/CustomerBanner/CustomerBanner';
import { useLayout } from '@/contexts/layout';
import { useGetCustomerBannerMessages } from '@/domain/content';
import { usePageScrollLock } from '@/hooks/usePageScrollLock';

import styles from './AppLayout.module.scss';

interface AppLayoutProps {
    /** Sidebar content (navigation, logo, etc.) */
    sidebar?: ReactNode;
    /** Header content (breadcrumb, user menu, etc.) */
    header?: ReactNode;
    /** Optional banner rendered above the header (same sticky region, same width as main column) */
    topBanner?: ReactNode;
    /** Main page content */
    children: ReactNode;
    /** Additional CSS class for the root element */
    className?: string;
}

export default function AppLayout(props: AppLayoutProps) {
    const { sidebar, header, topBanner, children, className } = props;
    const { t } = useTranslation();
    const { state, actions } = useLayout();
    const { sidebarOpen, sidebarCollapsed, isMobile } = state;
    const { closeSidebar } = actions;
    const [scrolled, setScrolled] = useState(false);

    // Customer banner messages
    const customerBannerMessages = useGetCustomerBannerMessages();

    // The drawer covers the page on a phone or tablet: following one of its links closes it,
    // and the page behind it does not scroll while it is open.
    const { pathname, search } = useLocation();
    const route = `${pathname}${search}`;
    const shownRoute = useRef(route);
    useEffect(() => {
        if (shownRoute.current === route) return;
        shownRoute.current = route;
        closeSidebar();
    }, [route, closeSidebar]);
    usePageScrollLock(isMobile && sidebarOpen);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div
            className={cn(styles.appLayout, sidebarCollapsed && styles.collapsed, className)}
            data-sidebar-collapsed={sidebarCollapsed}
            data-sidebar-mobile={isMobile}
            data-sidebar-open={sidebarOpen}>
            {/* Sidebar overlay backdrop (mobile only) */}
            {isMobile && (
                <div
                    className={cn(styles.sidebarOverlay, sidebarOpen && styles.overlayVisible)}
                    onClick={closeSidebar}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            closeSidebar();
                        }
                    }}
                    role="button"
                    tabIndex={sidebarOpen ? 0 : -1}
                    aria-label={t('layout.appLayout.closeSidebar')}
                    aria-hidden={!sidebarOpen}
                />
            )}

            {/* Sidebar */}
            {sidebar && (
                <aside className={cn(styles.sidebar, sidebarOpen && styles.sidebarOpen)}>
                    {sidebar}
                </aside>
            )}

            {/* Header region (sticky: topBanner + header, same width as main column) */}
            {(header || topBanner) && (
                <div className={styles.headerRegion}>
                    {topBanner && <div className={styles.topBanner}>{topBanner}</div>}
                    {header && (
                        <header className={cn(styles.header, scrolled && styles.scrolled)}>
                            {header}
                        </header>
                    )}
                </div>
            )}

            {/* Main content */}
            <main className={styles.content}>
                <CustomerBanner customerBannerMessages={customerBannerMessages} />
                {children}
            </main>
        </div>
    );
}
